import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import styled, { useTheme } from "styled-components";

import themeGet from "@styled-system/theme-get";

import { Button, Icon, Popover } from "orcs-design-system";
import SelectComponents from "src/components/SelectComponents";
import { getSearchPath } from "src/consts/urlPaths";
import { useClearFilters } from "src/contexts/filterAndFte/FilterAndFteContext";

import { trackEvent } from "src/services/segment";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import {
  filterTypes,
  positionOccupancyStatus,
} from "src/pages/SearchPage/SearchPage.config";
import HeaderSearchInput from "./headerSearch/HeaderSearchInput";
import HeaderSearchRecentsBridge from "./headerSearch/HeaderSearchRecentsBridge";
import HeaderSearchRecentsRecorder from "./headerSearch/HeaderSearchRecentsRecorder";
import {
  HEADER_DROPDOWN_RECENT_LIMIT,
  isBlankHeaderSearchTerm,
} from "./headerSearch/headerSearchRecentEntries";
import {
  headerPlaceholderText,
  placeholderText,
  reactSelectStyles,
  searchPagePlaceholderText,
} from "./Search.config";
import DropdownIndicator from "./sub-components/DropDownIndicator";
import SearchControl from "./sub-components/SearchControl";
import Menu from "./sub-components/Menu";
import MenuList from "./sub-components/MenuList";
import { useGlobalSearchContext } from "./GlobalSearchContext";

const SearchWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  [class$="-container"] {
    height: 100%;
  }
`;

const SearchFilter = styled(Button)`
  background-color: ${themeGet("colors.surfaceRaised")};
  color: ${themeGet("colors.subText")};
  border: none;
  border-left: 1px solid ${themeGet("colors.surfaceBorder")};
  border-radius: 0 ${themeGet("radii.2")} ${themeGet("radii.2")} 0;
  margin-right: 3px;
  font-size: ${themeGet("fontSizes.0")};
  height: 36px;
  margin-top: 0;
  display: flex;
  align-items: center;
  font-weight: 600;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  &:hover,
  &:focus {
    box-shadow: none;
    background-color: ${themeGet("colors.surfaceRaised")};
    color: ${themeGet("colors.danger")};
    border: none;
    border-left: 1px solid ${themeGet("colors.surfaceBorder")};
    svg,
    path {
      color: ${themeGet("colors.danger")};
    }
  }
`;

const SearchFilterTeamName = styled.span`
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchTypeClear = styled(SearchFilter)`
  background-color: transparent;
  border-left: none;
  color: ${themeGet("colors.mutedText")};
  font-weight: 400;
  padding-left: ${themeGet("space.s")};
  padding-right: ${themeGet("space.s")};

  &:hover,
  &:focus {
    background-color: transparent;
    border-left: none;
    color: ${themeGet("colors.bodyText")};
    svg,
    path {
      color: ${themeGet("colors.bodyText")};
    }
  }

  &:focus {
    box-shadow: ${themeGet("shadows.thickOutline")}
      ${themeGet("colors.primary30")};
  }
`;

// TODO: Move this to ORCs when finalised
const Search = ({
  isMulti,
  isCloseOnSelect,
  isLoading,
  defaultMenuIsOpen = false,
  onOptionSelect,
  searchError,
  onDefaultScopeClearCallback,
  isForSearchPage = false,
  searchType,
  resultsFormattedData,
  loadingPeople,
  loadingPositions,
  loadingTeams,
  loadingTags,
  showPositions = false,
  workspace,
  emptyStateComponent,
  onLoadMore,
  showHeaderRecents = false,
  persistHeaderSearchRecents = false,
}) => {
  const [paddingRight, setPaddingRight] = useState(0);
  const [menuTop, setMenuTop] = useState(null);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(
    Boolean(defaultMenuIsOpen)
  );
  const theme = useTheme();
  const history = useHistory();
  const selectRef = useRef(null);
  const searchContainerRef = useRef(null);
  const headerSearchRef = useRef(null);

  const overrideSelectStyles = useMemo(
    () => reactSelectStyles(theme, paddingRight, isLoading),
    [theme, paddingRight, isLoading]
  );

  const {
    defaultSearchScope,
    setDefaultSearchScope,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    setPeopleFilters,
    setGroupFilters,
    setDefaultTeamTypes,
    setPositionFilters,
    setPositionStatus,
    setIncludeHiddenTeams,
  } = useGlobalSearchContext();
  const clearFilters = useClearFilters();

  const filterRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (filterRef.current) {
      setPaddingRight(filterRef.current.clientWidth);
    } else {
      setPaddingRight(0);
    }
  });

  const updateMenuPosition = useCallback(() => {
    if (searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setMenuTop(rect.bottom);
    }
  }, []);

  const onMenuOpen = useCallback(() => {
    setHeaderMenuOpen(true);
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
  }, [updateMenuPosition]);

  const onMenuClose = useCallback(() => {
    setHeaderMenuOpen(false);
    headerSearchRef.current?.resetHighlight();
    setMenuTop(null);
    window.removeEventListener("resize", updateMenuPosition);
    window.removeEventListener("scroll", updateMenuPosition, true);
  }, [updateMenuPosition]);

  useEffect(() => {
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [updateMenuPosition]);

  const onChange = useCallback(
    (value) => {
      if (onOptionSelect) {
        onOptionSelect(value);
      }
    },
    [onOptionSelect]
  );

  const onInputChange = useCallback(
    (query, { action }) => {
      if (action === "input-change") {
        // On search page refresh, avoid overwriting before URL sync: skip "" when searchTerm not yet set
        if (
          isForSearchPage &&
          (searchTerm === undefined || searchTerm === null) &&
          query === ""
        ) {
          return query;
        }
        setSearchTerm(query);
        if (showHeaderRecents && !isBlankHeaderSearchTerm(query)) {
          headerSearchRef.current?.resetHighlight();
        }

        // Restore focus after React renders on search page (setTimeout 0 defers to next tick)
        if (isForSearchPage) {
          setTimeout(() => {
            selectRef.current?.focus?.();
          }, 0);
        }
      }
      return query;
    },
    [isForSearchPage, setSearchTerm, searchTerm, showHeaderRecents]
  );

  const onKeyDown = useCallback(
    (event) => {
      const { key } = event;
      const headerSearch = headerSearchRef.current;

      if (
        showHeaderRecents &&
        headerSearch &&
        !isForSearchPage &&
        isBlankHeaderSearchTerm(searchTerm) &&
        headerSearch.onInputKeyDown(event)
      ) {
        return;
      }

      // Keep focus when Enter is pressed on search page
      if (isForSearchPage && key === "Enter") {
        event.preventDefault();
        // setTimeout 0 runs after react-select's event handlers finish
        setTimeout(() => {
          selectRef.current?.focus?.();
        }, 0);
        return;
      }

      if (showHeaderRecents) {
        if (key === "Enter" && !isBlankHeaderSearchTerm(searchTerm)) {
          const query = searchTerm.trim();
          if (query) {
            headerSearch?.rememberSearch({ query, kind: "query" });
          }
          const searchPath = getSearchPath(undefined, searchTerm);
          history.push(searchPath);
        }
        return;
      }

      // Classic header search. Whitespace still submits, same as before recents.
      if (key === "Enter" && !isEmpty(searchTerm)) {
        const searchPath = getSearchPath(undefined, searchTerm);
        history.push(searchPath);
      }
    },
    [history, searchTerm, isForSearchPage, showHeaderRecents]
  );

  const onDefaultScopeClear = useCallback(() => {
    setDefaultSearchScope(null);
    onDefaultScopeClearCallback(searchTerm);
    // LEGACY until 2026-11-17
    trackEvent(EVENT_TRACKING.SEARCH_FILTERED, {
      setting: "scope",
      cleared: true,
    });
    trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_FILTER_COMPLETED, {
      lifecycle_stage: "completed",
      entry_point: "global_search",
      operation_intent: "search",
      filter_setting: "scope",
      filter_checked: false,
    });
  }, [setDefaultSearchScope, onDefaultScopeClearCallback, searchTerm]);

  const onClearSearchQuery = useCallback(() => {
    setSearchTerm("");
    if (!isForSearchPage) {
      return;
    }
    history.push(getSearchPath(searchType || filterTypes.ALL, "", false));
  }, [history, isForSearchPage, searchType, setSearchTerm]);

  const onSearchTypeClear = useCallback(() => {
    setPeopleFilters([]);
    setGroupFilters([]);
    setDefaultTeamTypes([]);
    setPositionFilters([]);
    setPositionStatus(positionOccupancyStatus.ALL);
    setIncludeHiddenTeams(false);
    // People refine + allocationStatus live in FilterAndFte and are shown on
    // people search. Teams/Tags/Positions Clear must not wipe those shared
    // Insight/workspace-persisted filters (localStorage) the user never saw.
    if (searchType === filterTypes.PEOPLE) {
      clearFilters();
    }
    history.push(getSearchPath(filterTypes.ALL, searchTerm, false));
    trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_FILTER_COMPLETED, {
      lifecycle_stage: "completed",
      entry_point: "global_search",
      operation_intent: "search",
      filter_setting: "filter",
      filter_value: searchType,
      filter_checked: false,
    });
  }, [
    clearFilters,
    history,
    searchTerm,
    searchType,
    setDefaultTeamTypes,
    setGroupFilters,
    setIncludeHiddenTeams,
    setPeopleFilters,
    setPositionFilters,
    setPositionStatus,
  ]);

  const hasSearchTypeFilter =
    isForSearchPage && searchType && searchType !== filterTypes.ALL;
  const showQueryClear =
    typeof searchTerm === "string" && searchTerm.trim().length > 0;
  // The query X owns the right side while a term is present, so it does not
  // sit next to the type Clear chip.
  const showTypeClear = hasSearchTypeFilter && !showQueryClear;
  const activePlaceholder = isForSearchPage
    ? searchPagePlaceholderText[searchType] || searchPagePlaceholderText.all
    : showHeaderRecents
      ? headerPlaceholderText
      : placeholderText;

  const filterOption = useCallback(
    () => true,
    [] // Server-side global search already returns filtered matches.
  );

  const customComponents = useMemo(
    () => ({
      ...SelectComponents,
      Control: SearchControl,
      DropdownIndicator,
      ...(showHeaderRecents ? { Input: HeaderSearchInput } : {}),
      Menu,
      MenuList,
    }),
    [showHeaderRecents]
  );

  const propsForCustomComponents = {
    menuTop,
    resultsFormattedData,
    loadingPeople,
    loadingPositions,
    loadingTeams,
    loadingTags,
    showPositions,
    debouncedSearchTerm,
    workspace,
    searchError,
    emptyStateComponent,
    onLoadMore,
    isForSearchPage,
    filterTypes,
    ...(showHeaderRecents ? { headerSearchTerm: searchTerm } : {}),
  };

  const searchForm = (
    <form data-testid="cp-search-form" style={{ height: "100%" }}>
      <SearchWrapper ref={searchContainerRef}>
        <Select
          ref={selectRef}
          name="global-search"
          inputId="global-search"
          cacheOptions={false}
          closeMenuOnSelect={isCloseOnSelect}
          closeMenuOnScroll={false}
          defaultMenuIsOpen={defaultMenuIsOpen}
          hideSelectedOptions={true}
          placeholder={activePlaceholder}
          onInputChange={onInputChange}
          onChange={onChange}
          isLoading={isLoading}
          options={[]}
          filterOption={filterOption}
          blurInputOnSelect={!isForSearchPage}
          openMenuOnFocus={!isForSearchPage}
          autoFocus={isForSearchPage}
          onKeyDown={onKeyDown}
          styles={overrideSelectStyles}
          aria-label={
            isForSearchPage
              ? activePlaceholder.replace("...", "")
              : showPositions
                ? "Type to search for people, positions, teams or tags"
                : "Type to search for people, teams or tags"
          }
          {...(showHeaderRecents ? {} : { role: "searchbox" })}
          isMulti={isMulti}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          form="_none"
          inputValue={searchTerm || ""}
          components={customComponents}
          onClearSearchQuery={onClearSearchQuery}
          {...propsForCustomComponents}
        />
        {showTypeClear ? (
          <SearchTypeClear
            ref={filterRef}
            type="button"
            small
            onClick={onSearchTypeClear}
            ariaLabel={`Clear ${searchType} search filter`}
            height="36px"
          >
            Clear
            <Icon icon={["fas", "times"]} color="mutedText" ml="xs" />
          </SearchTypeClear>
        ) : (
          !isEmpty(defaultSearchScope) && (
            <SearchFilter
              ref={filterRef}
              type="button"
              small
              variant="dangerAlternate"
              onClick={onDefaultScopeClear}
              height="36px"
            >
              <Popover
                text="Click to remove filter"
                direction="bottom"
                textAlign="center"
                width="140px"
              >
                <SearchFilterTeamName>
                  in: {defaultSearchScope.name}
                </SearchFilterTeamName>
                <Icon icon={["fas", "times"]} color="subText" ml="xs" />
              </Popover>
            </SearchFilter>
          )
        )}
      </SearchWrapper>
    </form>
  );

  if (showHeaderRecents && !isForSearchPage) {
    return (
      <HeaderSearchRecentsBridge
        apiRef={headerSearchRef}
        menuOpen={headerMenuOpen}
        visibleLimit={HEADER_DROPDOWN_RECENT_LIMIT}
      >
        {searchForm}
      </HeaderSearchRecentsBridge>
    );
  }

  if (persistHeaderSearchRecents) {
    return (
      <>
        <HeaderSearchRecentsRecorder />
        {searchForm}
      </>
    );
  }

  return searchForm;
};

Search.propTypes = {
  defaultMenuIsOpen: PropTypes.bool,
  isMulti: PropTypes.bool,
  isCloseOnSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  onOptionSelect: PropTypes.func,
  searchError: PropTypes.object,
  onDefaultScopeClearCallback: PropTypes.func,
  isForSearchPage: PropTypes.bool,
  searchType: PropTypes.oneOf(Object.values(filterTypes)),
  resultsFormattedData: PropTypes.object,
  loadingPeople: PropTypes.bool,
  loadingPositions: PropTypes.bool,
  loadingTeams: PropTypes.bool,
  loadingTags: PropTypes.bool,
  showPositions: PropTypes.bool,
  workspace: PropTypes.object,
  emptyStateComponent: PropTypes.node,
  onLoadMore: PropTypes.func,
  showHeaderRecents: PropTypes.bool,
  persistHeaderSearchRecents: PropTypes.bool,
};

export default Search;
